// src/utils/staleCache.js
//
// Tiny localStorage-backed stale-while-revalidate cache used by the
// Service dashboard tabs. Each entry stores `{ savedAt: epochMs, value }`
// under a namespaced key so we don't collide with other features.
//
// Pattern at the call site:
//   1. On mount / param change, read the cache. If hit, render
//      immediately and start a background refresh.
//   2. If miss, show the spinner and fetch.
//   3. When the user clicks Refresh, call `fetch?fresh=1` so the
//      backend also bypasses its own cache.
//
// Sizes: each PnL month is ~50-200 KB; the heaviest building period
// (24M) is ~1-2 MB. Browser localStorage limit is ~5-10 MB per origin,
// so even with the full grid of cached entries we're comfortably under.
// We swallow QuotaExceeded errors silently so the app keeps working
// uncached in the rare case the bucket fills up.

const PREFIX = 'hudson:cache:v1:';

// Nothing here is worth showing after this long. An entry older than the
// cap is dropped rather than rendered: a Jobber scan that has been failing
// for weeks used to leave the dashboard quietly displaying numbers from
// the last successful load, with no way for anyone to tell.
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export function cacheGet(key, maxAgeMs = MAX_AGE_MS) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > maxAgeMs) {
      localStorage.removeItem(PREFIX + key);
      return null;
    }
    return parsed; // { savedAt, value }
  } catch (e) {
    return null;
  }
}

export function cachePut(key, value) {
  try {
    localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ savedAt: Date.now(), value }),
    );
  } catch (e) {
    // QuotaExceeded or private-browsing — silently ignore. Worst case
    // we re-fetch on next mount instead of getting an instant render.
  }
}

export function cacheAgeMs(entry) {
  if (!entry || !entry.savedAt) return Infinity;
  return Date.now() - entry.savedAt;
}

// "5 min ago" / "2 hr ago" / "Just now" — used in the header to tell
// the operator how fresh the visible data is.
export function formatAge(ageMs) {
  if (!ageMs || ageMs < 30 * 1000) return 'just now';
  const sec = Math.floor(ageMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}
